import React, { useState, useRef, useEffect } from 'react';
import { FiX, FiCheck, FiAlertTriangle } from 'react-icons/fi';
// @ts-ignore - react-signature-canvas types may not be available
import SignatureCanvas from 'react-signature-canvas';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: (signatureData: {
    signature: string;
    printedName: string;
    date: string;
    email: string;
  }) => void;
  email: string;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose, onAccept, email }) => {
  const [printedName, setPrintedName] = useState('');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const signatureRef = useRef<SignatureCanvas>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPrintedName('');
      setHasScrolledToBottom(false);
      setTermsAccepted(false);
      setHasSignature(false);
      signatureRef.current?.clear();
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }
  }, [isOpen]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };


  const handleClearSignature = () => {
    signatureRef.current?.clear();
    setHasSignature(false);
  };

  const handleSignatureEnd = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setHasSignature(true);
    }
  };

  const handleAccept = () => {
    if (hasSignature && printedName.trim() && termsAccepted) {
      const signatureData = signatureRef.current?.toDataURL();
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      onAccept({
        signature: signatureData || '',
        printedName: printedName.trim(),
        date: currentDate,
        email: email
      });
    }
  };

  const canSubmit = hasSignature && printedName.trim() && termsAccepted && hasScrolledToBottom;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Terms & Conditions</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Please read and accept to continue</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div 
          ref={contentRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-6 space-y-6"
        >
          {/* Risk Disclosure */}
          <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <FiAlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              <h3 className="text-xl font-bold text-red-900 dark:text-red-100">
                TRADING RISK DISCLOSURE & LIABILITY RELEASE
              </h3>
            </div>
            <p className="text-red-800 dark:text-red-200 font-semibold mb-4">
              ⚠️ Please read carefully. Trading involves risk.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              By participating in the Trade Pilot AI Copy Trading Program, you acknowledge and agree to the following:
            </p>
          </div>

          {/* Disclosure Points */}
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
              <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">1. No Guaranteed Returns</h4>
              <p className="text-gray-700 dark:text-gray-300">
                The financial markets contain inherent risks. Past performance does not guarantee future results.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
              <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">2. Potential for Loss</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                You may experience losses—including loss of principal—while using this system.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Earn Your Profits™️, Trade Pilot™️, its owners, traders, agents, and software developers are not liable for any financial loss incurred through market activity.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
              <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">3. Copy Trading Risk</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                While our master trader uses expert-level strategies, you understand that:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
                <li>Market conditions change</li>
                <li>Signals may vary</li>
                <li>Execution times may differ</li>
                <li>Results may vary user to user</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
              <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">4. No Investment Advice</h4>
              <p className="text-gray-700 dark:text-gray-300">
                Earn Your Profits™️ does not provide personalized financial advice. This software provides automated signals and trade mirroring, not individualized guidance.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
              <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">5. You Maintain Full Control of Your Brokerage Account</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-3">You acknowledge:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
                <li>Your funds remain in your own account</li>
                <li>You can disconnect at any time</li>
                <li>You are solely responsible for deposits, withdrawals, and risk settings</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
              <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">6. Hold Harmless Agreement</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                You agree to hold Earn Your Profits™️, its affiliates, owners, employees, contractors, developers, and traders harmless of all liability, including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
                <li>Market losses</li>
                <li>Software issues</li>
                <li>Technology delays</li>
                <li>Incorrect predictions</li>
                <li>Connectivity issues</li>
                <li>System disruptions</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
              <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">7. Technology Disclaimer</h4>
              <p className="text-gray-700 dark:text-gray-300">
                You acknowledge that the AI system makes predictions based on algorithms, data models, and pattern recognition, but no prediction is guaranteed to be correct.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
              <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">8. Voluntary Participation</h4>
              <p className="text-gray-700 dark:text-gray-300">
                You are voluntarily choosing to use this system and understand all associated risks.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
              <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">9. No Refunds for Market Performance</h4>
              <p className="text-gray-700 dark:text-gray-300">
                The User understands that refunds will not be granted based solely on trading losses, market downturns, unprofitable trades, algorithm performance, account drawdowns, or dissatisfaction with market results. Refund requests, if any, may be reviewed on a case-by-case basis regarding billing or service-related issues only.
              </p>
            </div>
          </div>

          {/* Signature Page */}
          <div className="border-t-4 border-primary-500 pt-6 mt-8">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              AI TRADING SOFTWARE USER AGREEMENT & LIABILITY RELEASE
            </h3>

            {/* Single Checkbox - Covers All Terms */}
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-5">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative flex-shrink-0 mt-1">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                    termsAccepted
                      ? 'bg-green-500 border-green-500'
                      : 'border-gray-400 dark:border-gray-500 group-hover:border-green-400'
                  }`}>
                    {termsAccepted && (
                      <FiCheck className="h-4 w-4 text-white font-bold" />
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <span className="text-base font-semibold text-gray-900 dark:text-white block mb-2">
                    I have read and fully understand all the terms and conditions above, and I voluntarily agree to them.
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    By checking this box, you acknowledge that you have read the Trading Risk Disclosure, understand all risks associated with trading and automated/copy trading technology, understand that Earn Your Profits™️ does not guarantee profits, agree to hold Earn Your Profits™️ harmless from losses, damages, or legal claims, voluntarily choose to participate and accept full responsibility for all trading outcomes, confirm that you are in full control of your brokerage account, and understand that market results may vary and involve potential loss of principal.
                  </span>
                </div>
              </label>
            </div>

            {/* Signature Section */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Printed Name *
                </label>
                <input
                  type="text"
                  value={printedName}
                  onChange={(e) => setPrintedName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Signature *
                </label>
                <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white">
                  <SignatureCanvas
                    ref={signatureRef}
                    onEnd={handleSignatureEnd}
                    canvasProps={{
                      className: 'w-full h-40 rounded-lg',
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleClearSignature}
                  className="mt-2 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  Clear Signature
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Date
                  </label>
                  <input
                    type="text"
                    value={new Date().toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                  />
                </div>
              </div>
            </div>
          </div>

          {!hasScrolledToBottom && (
            <div className="sticky bottom-0 left-0 right-0 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pt-8 pb-4 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400 animate-bounce">
                ↓ Please scroll to the bottom to continue ↓
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAccept}
              disabled={!canSubmit}
              className={`px-8 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                canSubmit
                  ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              <FiCheck className="h-5 w-5" />
              I Accept & Agree
            </button>
          </div>
          {!hasScrolledToBottom && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-2 text-center">
              Please scroll through the entire document before accepting
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsModal;

